import { useCallback, useEffect, useState } from 'react';
import { getAccountAnimals } from '../../api/animalsApi';
import { adminAccountUse } from '../../api/inventoryApi';
import { getSpecimensByOwnership } from '../../api/specimensApi';
import { AccountSearch, FilterDropdown, Loader, NumberInput, Tooltip } from '../../components';
import { InfoSVG, PlusCircleSvg } from '../../components/svgs';
import AddStrawLevelDataModal from '../../components/AddStrawLevelData/AddStrawLevelDataModal';
import { FilterOption } from '../../components/custom-input/FilterDropdown';
import SpecimenLotInfo from '../../components/specimen-lot-info/SpecimenLotInfo';
import {
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  INVENTORY_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { showToast } from '../../services/toast.service';
import { AddStrawLevelDataModalMode, AddStrawModalState } from '../../types/interfaces/addStrawLevelData.interfaces';
import { Account, Animal, SpecimenInventory, SpecimenLocation, selectOption } from '../../types/interfaces';
import { dateFormatMMDDYYYY, redirectToTopOfThePage, specimenNeedsStrawDetails } from '../../utils/commonUtils';
import OverrideQuantityModal from './check-out/check-out-components/OverrideQuantityModal';
import './inventoryManagement.scss';
import { TransactionStatusEnum } from '../../types/enums';

const InventoryManagementClinicUse = (): JSX.Element => {
  const initialState = { value: '', name: '' };

  const [animalOptions, setAnimalOptions] = useState<FilterOption<Animal>[]>([]);
  const [childKey, setChildKey] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingSpecimens, setIsLoadingSpecimens] = useState<boolean>(false);
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState<boolean>(false);
  const [locationUseAmounts, setLocationUseAmounts] = useState<{ key: string; location: SpecimenLocation; amount: number }[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const [selectedAnimal, setSelectedAnimal] = useState<FilterOption<Animal>>();
  const [selectedSpecimenIndex, setSelectedSpecimenIndex] = useState<number>(-1);
  const [selectedSpecimenValue, setSelectedSpecimenValue] = useState<selectOption>(initialState);
  const [specimenOptions, setSpecimenOptions] = useState<selectOption[]>([initialState]);
  const [specimens, setSpecimens] = useState<SpecimenInventory[]>([]);
  const [selectedSpecimens, setSelectedSpecimens] = useState<{ key: string; specimen: SpecimenInventory; quantity: number }[]>(
    [],
  );
  const [usingInventory, setUsingInventory] = useState<boolean>(true);
  const [addStrawModalState, setAddStrawModalState] = useState<AddStrawModalState | null>(null);

  const isFemale = (specimen: SpecimenInventory) => specimen.animal?.gender?.toLowerCase() === 'female';

  const getSpecimenKey = useCallback((specimen: SpecimenInventory) => {
    if (isFemale(specimen) && specimen.embryoStrawId) {
      return `embryo-${specimen.embryoStrawId}`;
    }
    return `specimen-${specimen.specimenId}`;
  }, []);

  const hasFemaleSpecimen = specimens.some(specimen => isFemale(specimen));

  const totalSelectedUseQty = selectedSpecimens.reduce((total, selectedItem) => {
    return total + (selectedItem.quantity || 0);
  }, 0);

  const totalLocationUseAmounts = locationUseAmounts.reduce((total, locationAmount) => {
    return total + (locationAmount.amount || 0);
  }, 0);

  const isSemenQuantityValid = () => {
    return selectedSpecimens.every(selectedSpecimen => {
      const specimen = selectedSpecimen.specimen;

      if (isFemale(specimen)) {
        return true;
      }

      const locationTotal = locationUseAmounts
        .filter(locationAmount => locationAmount.key === selectedSpecimen.key)
        .reduce((sum, locationAmount) => sum + (locationAmount.amount || 0), 0);

      return selectedSpecimen.quantity === locationTotal;
    });
  };

  const isValid = () => {
    return (
      selectedAccount?.accountId &&
      selectedSpecimens.length > 0 &&
      totalSelectedUseQty > 0 &&
      locationUseAmounts.length > 0 &&
      totalLocationUseAmounts === totalSelectedUseQty &&
      locationUseAmounts.every(location => location.amount <= location.location.quantity) &&
      isSemenQuantityValid()
    );
  };

  const buildSpecimensPayload = () => {
    const specimenMap = new Map<
      number,
      {
        specimenId: number;
        selectedCanisterId: number;
        quantityUsed: number;
        selectedEmbryoStrawIds: number[];
      }
    >();
    locationUseAmounts
      .filter(locationAmount => locationAmount.amount > 0)
      .forEach(locationAmount => {
        const selectedSpecimen = selectedSpecimens.find(selectedItem => selectedItem.key === locationAmount.key);

        const specimenId = locationAmount.location.specimenId!;
        const embryoStrawId = selectedSpecimen?.specimen?.embryoStrawId;

        const existingPayload = specimenMap.get(specimenId);

        if (!existingPayload) {
          specimenMap.set(specimenId, {
            specimenId,
            selectedCanisterId: locationAmount.location.storageCanisterId,
            quantityUsed: locationAmount.amount,
            selectedEmbryoStrawIds: embryoStrawId ? [embryoStrawId] : [],
          });
          return;
        }
        existingPayload.quantityUsed += locationAmount.amount;
        if (embryoStrawId && !existingPayload.selectedEmbryoStrawIds.includes(embryoStrawId)) {
          existingPayload.selectedEmbryoStrawIds.push(embryoStrawId);
        }
      });

    return Array.from(specimenMap.values());
  };

  const handleSubmit = async () => {
    if (!usingInventory) return;

    const hasQuantityMismatch =
      (totalSelectedUseQty > 0 || totalLocationUseAmounts > 0) && totalLocationUseAmounts !== totalSelectedUseQty;

    const hasSemenMismatch = !isSemenQuantityValid();

    if (isValid()) {
      setUsingInventory(false);

      try {
        setIsLoading(true);
        setIsOverrideModalOpen(false);

        const specimensPayload = buildSpecimensPayload();
        await adminAccountUse(selectedAccount?.accountId!, {
          specimens: specimensPayload,
          notes,
        });
        showToast.success(toastMessages.SUCCESS);
        redirectToTopOfThePage();
        clearForm();
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setUsingInventory(true);
        setIsLoading(false);
      }
    } else if (hasQuantityMismatch || hasSemenMismatch) {
      setUsingInventory(true);
      showToast.error(toastMessages.TOTAL_QTY_MISMATCH);
    }
  };

  const clearForm = () => {
    setSelectedAccount(undefined);
    setSelectedAnimal(undefined);
    setAnimalOptions([]);
    setSpecimens([]);
    setSelectedSpecimens([]);
    setLocationUseAmounts([]);
    setNotes('');
    setChildKey(prev => prev + 1);
    setSelectedSpecimenIndex(-1);
    setSelectedSpecimenValue(initialState);
  };

  useEffect(() => {
    const createFilterOptions = (animals: Animal[]) => {
      return animals.map(
        (animal: Animal): FilterOption<Animal> => ({
          name: `${animal.code}-${animal.name}`,
          value: animal,
        }),
      );
    };

    const resetAnimalSelection = () => {
      setAnimalOptions([]);
      setSelectedAnimal(undefined);
      setSpecimens([]);
      setSelectedSpecimens([]);
      setLocationUseAmounts([]);
      setSelectedSpecimenIndex(-1);
      setSelectedSpecimenValue(initialState);
    };

    const getOwnedAnimals = async () => {
      resetAnimalSelection();
      if (selectedAccount?.accountId) {
        try {
          const { data } = await getAccountAnimals(selectedAccount.accountId as number, {
            include: 'Breed.Species, Specimens, Owners',
          });
          setAnimalOptions(createFilterOptions(data as Animal[]));
        } catch {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    };
    getOwnedAnimals();
  }, [selectedAccount]);

  const fetchSpecimens = useCallback(async () => {
    if (!selectedAnimal?.value?.animalId || !selectedAccount?.accountId) {
      setSpecimens([]);
      setSpecimenOptions([initialState]);
      return;
    }

    setIsLoadingSpecimens(true);
    try {
      setSelectedSpecimens([]);
      setLocationUseAmounts([]);
      setSelectedSpecimenValue(initialState);
      setSelectedSpecimenIndex(-1);

      const params = {
        include: 'Animal, SpecimenLocation, EmbryoStraw, SpecimenType',
        excludeStatuses: [TransactionStatusEnum.Pending, TransactionStatusEnum.Hold, TransactionStatusEnum.Processed],
      };
      const { data } = await getSpecimensByOwnership(selectedAnimal.value.animalId, selectedAccount.accountId, params);

      const flattened: (SpecimenInventory & { embryoStrawCode?: string })[] = [];
      data.forEach((specimen: SpecimenInventory) => {
        if (specimen.animal?.gender?.toLowerCase() === 'female' && specimen.embryoStraws?.length) {
          specimen.embryoStraws.forEach(straw => {
            flattened.push({
              ...specimen,
              embryoStrawId: straw.embryoStrawId,
              embryoStrawCode: straw.embryoStrawCode,
              embryoGrade: straw.embryoGrade,
              embryoStage: straw.embryoStage,
              embryoStrawNumber: straw.embryoStrawNumber,
              quantityAvailable: straw.embryoCountPerStraw || 0,
              specimenLocations:
                specimen?.specimenLocations?.filter(loc => loc.storageCanisterId === straw.storageCanisterId) ?? [],
            });
          });
        } else {
          flattened.push({
            ...specimen,
            embryoStrawCode: '',
          });
        }
      });

      setSpecimens(flattened);

      setSpecimenOptions(
        flattened.map((specimen, index) => ({
          name: specimen.embryoStrawCode
            ? `${specimen.animal?.code}-${specimen.animal?.name}-${new Date(
                specimen.freezeDate,
              ).toLocaleDateString()} - Straw: ${specimen.embryoStrawCode} - ${specimen.embryoStrawNumber ?? '-'}`
            : `${specimen.animal?.code}-${specimen.animal?.name}-${new Date(specimen.freezeDate).toLocaleDateString()} - ${
                specimen?.lotNumber ? `${specimen.specimenId} (${specimen.lotNumber})` : specimen.specimenId
              }`,
          value: `${index}`,
        })),
      );
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoadingSpecimens(false);
    }
  }, [selectedAnimal, selectedAccount]);

  useEffect(() => {
    fetchSpecimens();
  }, [fetchSpecimens]);

  const handleAddStrawOpen = (specimenId: number) => {
    setAddStrawModalState({ specimenId });
  };

  const renderStrawLotContent = (specimen: SpecimenInventory) => {
    if (specimen.embryoStrawCode) {
      return (
        <>
          {specimen.embryoStrawCode}
          <Tooltip>
            {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${specimen.embryoGrade || 'NA'}`} <br />
            {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${specimen.embryoStage || 'NA'}`}
          </Tooltip>
        </>
      );
    }

    if (specimenNeedsStrawDetails(specimen)) {
      return (
        <button
          className="button icon add-straw-details"
          type="button"
          onClick={() => handleAddStrawOpen(specimen.specimenId)}>
          <PlusCircleSvg />
        </button>
      );
    }

    return '-';
  };

  useEffect(() => {
    setLocationUseAmounts(previousLocations => {
      const updatedLocations = [...previousLocations];

      selectedSpecimens.forEach(({ specimen, key: specimenKey }) => {
        specimen.specimenLocations?.forEach(specimenLocation => {
          const alreadyExists = updatedLocations.some(
            existingLocation =>
              existingLocation.key === specimenKey &&
              existingLocation.location.storageCanisterId === specimenLocation.storageCanisterId,
          );

          if (!alreadyExists) {
            updatedLocations.push({
              key: specimenKey,
              location: specimenLocation,
              amount: isFemale(specimen) ? specimen.quantityAvailable ?? 0 : 0,
            });
          }
        });
      });

      return updatedLocations.filter(existingLocation =>
        selectedSpecimens.some(selectedSpecimen => selectedSpecimen.key === existingLocation.key),
      );
    });
  }, [selectedSpecimens]);

  const handleSpecimenRowClass = (specimen: SpecimenInventory): string => {
    const key = getSpecimenKey(specimen);
    const selected = selectedSpecimens.find(selectedItem => selectedItem.key === key);

    if (!selected) return 'specimen-row';

    const isOverOwnedQuantity = selected.quantity > specimen.quantityOwned;
    const isOverAvailableQuantity = selected.quantity > specimen.quantityAvailable;

    if (isOverOwnedQuantity) {
      showToast.error(toastMessages.CHOSE_MORE_THAN_OWNED);
      return 'specimen-row invalid-row';
    }
    if (isOverAvailableQuantity) {
      return 'specimen-row override-row';
    }
    return 'specimen-row';
  };

  const handleLocationRowClass = useCallback(
    (location: SpecimenLocation, key: string): string => {
      const current = locationUseAmounts.find(
        locationAmount => locationAmount.key === key && locationAmount.location.storageCanisterId === location.storageCanisterId,
      );
      if (current && current?.amount > location.quantity) {
        showToast.error(toastMessages.CHOSE_MORE_THAN_TOTAL);
        return 'specimen-row invalid-row';
      } else {
        return 'specimen-row';
      }
    },
    [locationUseAmounts],
  );

  const handleSpecimenLocationAmountChange = (key: string, canisterId: number, newAmount: number) => {
    setLocationUseAmounts(prev =>
      prev.map(location =>
        location.key === key && location.location.storageCanisterId === canisterId
          ? { ...location, amount: newAmount }
          : location,
      ),
    );
  };
  const isSpecimenValid = (specimen: SpecimenInventory) => {
    if (specimenNeedsStrawDetails(specimen)) {
      return true;
    }

    if (isFemale(specimen)) {
      return (specimen.quantityAvailable ?? 0) > 0;
    }
    return true;
  };

  const filteredSpecimenOptions = specimenOptions.filter(option => {
    if (option.value === '') return true;
    const specimen = specimens[Number(option.value)];
    return specimen ? isSpecimenValid(specimen) : false;
  });

  const renderLocationTableRows = (specimen: SpecimenInventory, key: string, location: SpecimenLocation) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const rowClass = `${handleLocationRowClass(location, key)} ${isTravelingTank ? 'disabled' : ''}`;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name}/${location.storageCanister?.storageTank?.name}/${location.storageCanister?.name}`;
    const animalDetails = `${selectedAnimal?.value.code} - ${dateFormatMMDDYYYY(specimen.freezeDate)}`;
    const currentAmount =
      locationUseAmounts.find(l => l.key === key && l.location.storageCanisterId === location.storageCanisterId)?.amount ?? 0;

    return (
      <tr className={rowClass} key={`${key}-loc-${location?.storageCanisterId}`}>
        <td>{locationPath}</td>
        <td>{animalDetails}</td>
        <td>{location.quantity}</td>

        {hasFemaleSpecimen && <td>{specimen.embryoStrawCode || '-'}</td>}
        <td>
          {isTravelingTank ? (
            LABEL_CONSTANTS.TRAVELING_TANK
          ) : isFemale(specimen) ? (
            <NumberInput value={specimen.quantityAvailable} />
          ) : (
            <NumberInput
              onChange={e => handleSpecimenLocationAmountChange(key, location.storageCanisterId, Number(e.target.value))}
              value={currentAmount.toString()}
            />
          )}
        </td>
      </tr>
    );
  };

  const renderLocationMobileRow = (specimen: SpecimenInventory, key: string, location: SpecimenLocation) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name} / ${location.storageCanister?.storageTank?.name} / ${location.storageCanister?.name}`;
    const animalDetails = `${selectedAnimal?.value.code} - ${dateFormatMMDDYYYY(specimen.freezeDate)}`;
    const currentAmount =
      locationUseAmounts.find(l => l.key === key && l.location.storageCanisterId === location.storageCanisterId)?.amount ?? 0;

    return (
      <div className="selected-storage-location" key={`${key}-loc-${location.storageCanisterId}`}>
        <div className={`card max-width ${isTravelingTank ? 'disabled' : ''}`}>
          <div className="body">
            <div className="body-content">
              <div className="selected-location">
                <label>{TABLE_HEADER_CONSTANTS.LOCATION}:</label>
                <label>{locationPath}</label>
              </div>
              <div className="location-item">
                <label>{TABLE_HEADER_CONSTANTS.ITEM}:</label>
                <label>{animalDetails}</label>
              </div>
            </div>
            <hr />
            <div className="body-content">
              <div className="canister-qty">
                <label>{TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}</label>
                <label>{location.quantity}</label>
              </div>
              <div className="actual-quantity">
                {isTravelingTank ? (
                  <label>{LABEL_CONSTANTS.TRAVELING_TANK}</label>
                ) : isFemale(specimen) ? (
                  <>
                    <label>{TABLE_HEADER_CONSTANTS.USE_QTY}</label>
                    <NumberInput value={specimen.quantityAvailable} />
                  </>
                ) : (
                  <>
                    <label>{TABLE_HEADER_CONSTANTS.USE_QTY}</label>
                    <NumberInput
                      onChange={e => handleSpecimenLocationAmountChange(key, location.storageCanisterId, Number(e.target.value))}
                      value={currentAmount.toString()}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const toggleSpecimenSelection = (specimen: SpecimenInventory, checked: boolean) => {
    const key = getSpecimenKey(specimen);

    setSelectedSpecimens(prev => {
      if (checked) {
        if (prev.some(selectedItem => selectedItem.key === key)) return prev;

        return [
          ...prev,
          {
            key,
            specimen,
            quantity: isFemale(specimen) ? specimen.quantityAvailable ?? 0 : 0,
          },
        ];
      }
      return prev.filter(selectedItem => selectedItem.key !== key);
    });
  };

  const hasOverrideQty = selectedSpecimens.some(
    selectedItem => selectedItem.quantity > (selectedItem.specimen.quantityAvailable ?? 0),
  );

  const filteredSpecimens = specimens?.filter(isSpecimenValid) ?? [];

  return (
    <div className="inventory-management inventory-management-clinic-use">
      <div className="inventory-action card">
        {isLoading && <Loader pageLoader loaderSize={'medium'} simple />}
        <OverrideQuantityModal
          showModal={isOverrideModalOpen}
          setShowModal={setIsOverrideModalOpen}
          handleSubmit={handleSubmit}
        />
        <h1>{PAGE_HEADER_CONSTANTS.CLINIC_USE}</h1>
        <div className="info-banner">
          <InfoSVG size="18" color="#0f64dcf5" />
          {INVENTORY_CONSTANTS.EMBRYO_AUDIT_PROGRESS_MESSAGE_FOR_ADMIN}
        </div>
        <h2>{LABEL_CONSTANTS.INFORMATION}</h2>
        <div className="form-row">
          <label>{LABEL_CONSTANTS.ACCOUNT_OWNER}:</label>
          <div className="input-container">
            <AccountSearch key={childKey} showIcon onChange={(account: Account | undefined) => setSelectedAccount(account)} />
          </div>
        </div>
        <div className="form-row">
          <label>{ANIMAL_CONSTANTS.ANIMAL_NAME}:</label>
          <div className="input-container">
            <FilterDropdown
              options={animalOptions}
              value={selectedAnimal ?? { name: '', value: undefined }}
              disabled={animalOptions.length === 0 || !selectedAccount}
              onChange={setSelectedAnimal}
            />
          </div>
        </div>
        <br />

        <h2>{LABEL_CONSTANTS.SELECT_ITEM}</h2>
        {selectedAnimal && selectedAccount && (
          <div className="clinic-use-select-item">
            <div className="desk-select-item">
              <div className={'specimen-table'}>
                <table>
                  <thead>
                    <tr>
                      <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                      <th>{TABLE_HEADER_CONSTANTS.LOT_DATE_NO}</th>
                      {hasFemaleSpecimen && (
                        <>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                        </>
                      )}
                      <th>{TABLE_HEADER_CONSTANTS.OWNED_QTY}</th>
                      <th>{hasFemaleSpecimen ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                      <th>{TABLE_HEADER_CONSTANTS.USE_QTY}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isLoadingSpecimens && filteredSpecimens.length === 0 && (
                      <tr>
                        <td colSpan={hasFemaleSpecimen ? 7 : 5} className="no-records">
                          {LABEL_CONSTANTS.NO_RESULTS_FOUND}
                        </td>
                      </tr>
                    )}

                    {!isLoadingSpecimens &&
                      filteredSpecimens.map(specimen => {
                        const key = getSpecimenKey(specimen);
                        const isSelected = selectedSpecimens.some(selectedSpecimen => selectedSpecimen.key === key);
                        return (
                          <tr className={handleSpecimenRowClass(specimen)} key={`specimen-row-${key}`}>
                            <td>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={specimenNeedsStrawDetails(specimen)}
                                onChange={e => toggleSpecimenSelection(specimen, e.target.checked)}
                              />
                            </td>
                            <td>
                              <SpecimenLotInfo
                                specimenId={specimen.specimenId}
                                lotNumber={specimen?.lotNumber}
                                freezeDate={specimen.freezeDate}
                              />
                            </td>
                            {hasFemaleSpecimen && (
                              <>
                                <td>{renderStrawLotContent(specimen)}</td>

                                <td>{specimen?.embryoStrawNumber ?? '-'}</td>
                              </>
                            )}
                            <td>{specimen.quantityOwned}</td>
                            <td>{specimen.quantityAvailable}</td>
                            <td>
                              {isFemale(specimen) ? (
                                <span>{specimen.quantityAvailable}</span>
                              ) : (
                                <NumberInput
                                  disabled={!isSelected}
                                  value={
                                    selectedSpecimens.find(selectedItem => selectedItem.key === key)?.quantity?.toString() ?? ''
                                  }
                                  onChange={e =>
                                    setSelectedSpecimens(prev =>
                                      prev.map(selectedItem =>
                                        selectedItem.key === key
                                          ? { ...selectedItem, quantity: Number(e.target.value) }
                                          : selectedItem,
                                      ),
                                    )
                                  }
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    {isLoadingSpecimens && (
                      <tr>
                        <td colSpan={6}>
                          <>
                            <br />
                            <br />
                            <Loader loaderSize="small" />
                            <br />
                            <br />
                          </>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={hasFemaleSpecimen ? 6 : 4}></td>
                      <td colSpan={1}>
                        <div className="quantity total-to-use">{totalSelectedUseQty}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/** Mobile View */}
            <div className="xs-select-item">
              {filteredSpecimens.map(specimen => {
                const key = getSpecimenKey(specimen);
                const selectedSpecimen = selectedSpecimens.find(s => s.key === key);

                return (
                  <div key={key} className="selected-specimen">
                    <div className={`card max-width ${selectedSpecimen ? 'selected' : ''}`}>
                      <div className="body">
                        <div className="checkbox-column">
                          <input
                            type="checkbox"
                            checked={!!selectedSpecimen}
                            disabled={specimenNeedsStrawDetails(specimen)}
                            onChange={e => toggleSpecimenSelection(specimen, e.target.checked)}
                          />
                        </div>

                        <div className="selected-account">
                          <label>{TABLE_HEADER_CONSTANTS.ACCOUNT_BUSINESS_FARM}:</label>
                          <label>{selectedAccount.name}</label>
                        </div>
                        <div className="owned-quantity">
                          <label>{TABLE_HEADER_CONSTANTS.OWNED_QTY}:</label>
                          <label>{specimen.quantityOwned}</label>
                        </div>
                        <div className="available-quantity">
                          <label>{TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}:</label>
                          <label>{specimen.quantityAvailable}</label>
                        </div>
                        <div className="actual-quantity">
                          <label>{TABLE_HEADER_CONSTANTS.USE_QTY}:</label>
                          {isFemale(specimen) ? (
                            <span>{specimen.quantityAvailable}</span>
                          ) : (
                            <NumberInput
                              disabled={!selectedSpecimen}
                              value={selectedSpecimen?.quantity?.toString() ?? ''}
                              onChange={e =>
                                setSelectedSpecimens(prev =>
                                  prev.map(item => (item.key === key ? { ...item, quantity: Number(e.target.value) } : item)),
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="total-quantity">
                <label>{LABEL_CONSTANTS.TOTAL_QTY_TO_USE} :&nbsp;</label>
                {totalSelectedUseQty}
              </div>
            </div>
          </div>
        )}
        <br />

        <h2>{LABEL_CONSTANTS.ITEM_STORAGE_LOCATIONS}</h2>
        {selectedAnimal && selectedAccount && selectedSpecimens.length > 0 && (
          <div className="clinic-use-storage-locations">
            <div className="desk-storage-locations-table">
              <div className="specimen-table" key={childKey}>
                <table>
                  <thead>
                    <tr>
                      <th>{TABLE_HEADER_CONSTANTS.LOCATION}</th>
                      <th>{TABLE_HEADER_CONSTANTS.ITEM}</th>
                      <th>{TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}</th>
                      {hasFemaleSpecimen && <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>}
                      <th>{TABLE_HEADER_CONSTANTS.USE_QTY}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSpecimens.map(({ specimen, key }) =>
                      specimen.specimenLocations?.map(location => renderLocationTableRows(specimen, key, location)),
                    )}
                    <tr className={totalLocationUseAmounts > totalSelectedUseQty ? 'invalid-row' : ''}>
                      <td colSpan={hasFemaleSpecimen ? 4 : 3}></td>
                      <td colSpan={1}>
                        <div className={'quantity storage-total'}>{totalLocationUseAmounts}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/** Mobile View */}
            <div className="xs-storage-locations-table">
              {selectedSpecimens.map(({ specimen, key }) =>
                specimen.specimenLocations?.map(location => renderLocationMobileRow(specimen, key, location)),
              )}
              <div className="storage-total">
                <label>{LABEL_CONSTANTS.SELECTED_QTY_TO_USE}:&nbsp;</label>
                {totalLocationUseAmounts}
              </div>
            </div>
          </div>
        )}
        <br />

        <div className="form-row expanded-text-area">
          <label htmlFor="notes">{LABEL_CONSTANTS.NOTES}</label>
          <textarea
            id="notes"
            name="notes"
            placeholder={LABEL_CONSTANTS.NOTES}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={4}
            cols={50}
          />
        </div>
        <br />
        <div className="submit-button">
          <button
            onClick={() => {
              if (hasOverrideQty) {
                setIsOverrideModalOpen(true);
              } else {
                handleSubmit();
              }
            }}
            className={`button green small ${!isValid() && 'disabled'}`}>
            {BUTTON_CONSTANTS.SUBMIT}
          </button>
        </div>
      </div>

      {addStrawModalState && (
        <AddStrawLevelDataModal
          isOpen
          onClose={() => setAddStrawModalState(null)}
          onSuccess={fetchSpecimens}
          mode={AddStrawLevelDataModalMode.Specimen}
          specimenIds={[addStrawModalState.specimenId]}
          preselectedSpecimenId={addStrawModalState.specimenId}
        />
      )}
    </div>
  );
};

export default InventoryManagementClinicUse;
